import type { Experiment } from 'types';
import type { PxlScores } from './pxl-scores';
import { Edit } from 'lucide-react';
import { cn } from 'helpers/cn';
import { Button } from '@/components/ui/button';
import { SectionAuthor } from '../SectionAuthor';

type PXLScoreDisplayProps = {
  experiment: Pick<Experiment, 'sectionAuthors'>;
  scores: PxlScores | null | undefined;
  onEdit: () => void;
};

const getScoreLabel = (score: number) => {
  if (score <= 1) return 'Very Low';
  if (score <= 2) return 'Low';
  if (score <= 3) return 'Moderate';
  if (score <= 4) return 'High';
  return 'Very High';
};

/**
 * The score is a verdict, so it is coloured like one: a low priority reads as negative,
 * the middle band as something still to weigh up, and a strong score as positive.
 */
const getScoreTone = (score: number) => {
  if (score <= 2) return { dot: 'bg-danger', text: 'text-danger' };
  if (score <= 3) return { dot: 'bg-warn', text: 'text-warn' };
  if (score <= 4) return { dot: 'bg-accent', text: 'text-accent' };
  return { dot: 'bg-ok', text: 'text-ok' };
};

const DIMENSIONS = [
  { key: 'p' as const, label: 'Potential', short: 'P' },
  { key: 'x' as const, label: 'Exposure', short: 'X' },
  { key: 'l' as const, label: 'Effort', short: 'L' },
];

export const PXLScoreDisplay = ({ experiment, scores, onEdit }: PXLScoreDisplayProps) => {
  // Reading .p straight off an absent score set is what crashed the Info tab.
  // Callers are expected to pass a complete set, but bail rather than throw.
  if (!scores) return null;

  const finalScore = (scores.p + scores.x + scores.l) / 3;
  const finalScoreFormatted = finalScore.toFixed(1);
  const finalScorePercent = (finalScore / 5) * 100;
  const finalTone = getScoreTone(finalScore);

  return (
    <section className="bg-panel border-line rounded-panel border" data-tour="brief-importance">
      <div className="border-line flex items-start justify-between gap-3 border-b px-4 py-3">
        <div className="min-w-0">
          <h2 className="micro-label text-foreground">Importance — PXL score</h2>
          <p className="supporting mt-1">Priority score calculated using the PXL framework.</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* Rated, so the section has content: this header is only shown for a complete score. */}
          <SectionAuthor experiment={experiment} section="pxl" hasContent />
          <Button
            variant="outline"
            size="sm"
            onClick={onEdit}
            className="border-line text-foreground hover:bg-hover h-8 shrink-0 rounded-lg border bg-transparent px-3 text-meta"
          >
            <Edit className="size-3.5" strokeWidth={1.5} />
            Edit
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-4 p-4">
        <div className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between gap-2">
            <span className={cn('metric-value', finalTone.text)}>{finalScoreFormatted}</span>
            <span className="micro-label">out of 5.0 · {getScoreLabel(finalScore)} priority</span>
          </div>
          <div className="bg-track h-[3px] w-full overflow-hidden rounded-pill">
            <div className={cn('h-full rounded-pill', finalTone.dot)} style={{ width: `${finalScorePercent}%` }} />
          </div>
        </div>

        {/* Two columns on a phone, three from `sm`: at 375px a third of the row is
            105px, which truncates every dimension name down to its first word. */}
        <div className="border-line grid grid-cols-2 gap-2 border-t pt-4 sm:grid-cols-3">
          {DIMENSIONS.map(({ key, label, short }) => {
            const tone = getScoreTone(scores[key]);
            return (
              <div key={key} className="border-line bg-panel2 rounded-card flex flex-col gap-1 border p-3">
                <span className="micro-label truncate" title={`${label} (${short})`}>
                  {label} ({short})
                </span>
                <span className="flex items-center gap-2">
                  <span className={cn('size-[5px] shrink-0 rounded-pill', tone.dot)} />
                  <span className="num text-foreground">{scores[key]} / 5</span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
